/**
 * Traingle — Core Theme JavaScript
 * Handles sticky navigation, mobile drawer, accordions, unit toggles,
 * quick view modal, recently viewed products, and sartorial style quiz.
 */

document.addEventListener('DOMContentLoaded', () => {
  // 1. Sticky Header Effect
  const header = document.querySelector('.site-header');
  if (header) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 40) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }, { passive: true });
  }

  // 2. Mobile Navigation Drawer (Three-Line Hamburger Menu)
  function openMobileNav() {
    const mobileDrawer = document.getElementById('MobileNavDrawer');
    const backdrop = document.getElementById('DrawerBackdrop');
    if (mobileDrawer) {
      mobileDrawer.classList.add('is-open');
    }
    if (backdrop) {
      backdrop.classList.add('is-open');
    }
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    const mobileDrawer = document.getElementById('MobileNavDrawer');
    const backdrop = document.getElementById('DrawerBackdrop');
    if (mobileDrawer) {
      mobileDrawer.classList.remove('is-open');
    }
    if (backdrop && !document.querySelector('.cart-drawer.is-open')) {
      backdrop.classList.remove('is-open');
    }
    document.body.style.overflow = '';
  }

  window.openMobileNav = openMobileNav;
  window.closeMobileNav = closeMobileNav;

  // Global click event delegation for mobile menu opening & closing
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('.mobile-menu-toggle, [data-action="open-mobile-nav"]');
    if (toggle) {
      e.preventDefault();
      openMobileNav();
      return;
    }
    const closeBtn = e.target.closest('[data-action="close-mobile-nav"]');
    if (closeBtn) {
      e.preventDefault();
      closeMobileNav();
      return;
    }
    const backdrop = document.getElementById('DrawerBackdrop');
    if (e.target === backdrop) {
      closeMobileNav();
    }
  });

  // 3. Accordions
  document.addEventListener('click', (e) => {
    const headerBtn = e.target.closest('.accordion-header');
    if (headerBtn) {
      const item = headerBtn.closest('.accordion-item');
      const body = item.querySelector('.accordion-body');
      const isOpen = item.classList.contains('is-open');

      // Close siblings if in exclusive accordion
      const parentAccordion = item.closest('[data-accordion-single]');
      if (parentAccordion) {
        parentAccordion.querySelectorAll('.accordion-item').forEach(sibling => {
          sibling.classList.remove('is-open');
          const sibBody = sibling.querySelector('.accordion-body');
          if (sibBody) sibBody.style.maxHeight = null;
        });
      }

      if (!isOpen) {
        item.classList.add('is-open');
        body.style.maxHeight = body.scrollHeight + 'px';
      } else {
        item.classList.remove('is-open');
        body.style.maxHeight = null;
      }
    }
  });

  // 4. Size Guide Unit Switcher (Inches <-> CM)
  document.addEventListener('click', (e) => {
    const unitBtn = e.target.closest('[data-unit-toggle]');
    if (unitBtn) {
      const unit = unitBtn.getAttribute('data-unit-toggle');
      document.querySelectorAll('[data-unit-toggle]').forEach(b => b.classList.remove('is-active'));
      unitBtn.classList.add('is-active');

      document.querySelectorAll('[data-measurement-in]').forEach(el => {
        if (unit === 'cm') {
          el.textContent = el.getAttribute('data-measurement-cm');
        } else {
          el.textContent = el.getAttribute('data-measurement-in');
        }
      });
    }
  });

  // 5. Quick View Modal
  const quickViewModal = document.getElementById('QuickViewModal');
  const quickViewContent = document.getElementById('QuickViewContent');

  document.addEventListener('click', async (e) => {
    const qvBtn = e.target.closest('[data-action="quick-view"]');
    if (qvBtn) {
      e.preventDefault();
      const handle = qvBtn.getAttribute('data-product-handle');
      if (!handle || !quickViewModal) return;

      quickViewModal.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      if (quickViewContent) {
        quickViewContent.innerHTML = '<div style="padding: 3rem; text-align: center;">Loading garment specifications...</div>';
      }

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('Product not found');
        const product = await res.json();
        renderQuickView(product);
      } catch (err) {
        if (quickViewContent) {
          quickViewContent.innerHTML = '<div style="padding: 2rem; color: var(--color-error);">Unable to load product preview.</div>';
        }
      }
    }

    const closeQv = e.target.closest('[data-action="close-quick-view"]');
    if (closeQv || (e.target === quickViewModal)) {
      if (quickViewModal) quickViewModal.classList.remove('is-open');
      if (backdrop && !document.querySelector('.cart-drawer.is-open')) {
        backdrop.classList.remove('is-open');
      }
    }
  });

  function renderQuickView(product) {
    if (!quickViewContent) return;
    const formatMoney = (cents) => {
      const formatted = (cents / 100).toFixed(2);
      return (window.Traingle?.moneyFormat || window.ModernGentleman?.moneyFormat || '₹{{amount}}').replace('{{amount}}', formatted);
    };

    quickViewContent.innerHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; align-items: start;">
        <div>
          <img src="${product.featured_image || ''}" alt="${product.title}" style="width: 100%; border-radius: 4px; aspect-ratio: 3/4; object-fit: cover;">
        </div>
        <div>
          <span style="font-size: 0.75rem; color: var(--color-electric-blue); font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em;">${product.vendor || 'Traingle'}</span>
          <h3 style="margin-top: 0.5rem; margin-bottom: 0.75rem; font-family: var(--font-heading);">${product.title}</h3>
          <div style="font-size: 1.25rem; font-weight: 700; color: var(--color-primary-navy); margin-bottom: 1.25rem;">${formatMoney(product.price)}</div>
          <div style="color: var(--color-slate-dark); font-size: 0.875rem; line-height: 1.6; margin-bottom: 1.5rem;">${product.description ? product.description.replace(/<[^>]*>/g, '').slice(0, 180) + '...' : ''}</div>
          
          <form data-type="add-to-cart-form" style="display: flex; flex-direction: column; gap: 1rem;">
            <input type="hidden" name="id" value="${product.variants[0].id}">
            <button type="submit" class="btn btn-primary btn-full">Add to Bag — ${formatMoney(product.price)}</button>
            <a href="${product.url}" class="btn btn-outline btn-full" style="text-align: center;">View Full Atelier Details</a>
          </form>
        </div>
      </div>
    `;
  }

  // 6. Recently Viewed Products (LocalStorage)
  const currentProductData = document.getElementById('CurrentProductMeta');
  if (currentProductData) {
    try {
      const p = JSON.parse(currentProductData.textContent);
      let viewed = JSON.parse(localStorage.getItem('mg_recently_viewed') || '[]');
      viewed = viewed.filter(item => item.id !== p.id);
      viewed.unshift(p);
      if (viewed.length > 6) viewed = viewed.slice(0, 6);
      localStorage.setItem('mg_recently_viewed', JSON.stringify(viewed));
    } catch (e) {
      console.warn('Could not store recently viewed product:', e);
    }
  }

  const recentlyViewedContainer = document.getElementById('RecentlyViewedGrid');
  if (recentlyViewedContainer) {
    try {
      const viewed = JSON.parse(localStorage.getItem('mg_recently_viewed') || '[]');
      const filtered = viewed.filter(item => !currentProductData || item.id !== JSON.parse(currentProductData.textContent).id);
      if (filtered.length > 0) {
        recentlyViewedContainer.innerHTML = filtered.map(item => `
          <div class="product-card">
            <div class="product-card__media">
              <a href="${item.url}">
                <img src="${item.image}" alt="${item.title}" class="product-card__image" loading="lazy">
              </a>
            </div>
            <div class="product-card__info">
              <h4 class="product-card__title"><a href="${item.url}">${item.title}</a></h4>
              <div class="product-card__price">
                <span class="price-regular">${item.price}</span>
              </div>
            </div>
          </div>
        `).join('');
        const section = document.getElementById('RecentlyViewedSection');
        if (section) section.style.display = 'block';
      }
    } catch (e) {
      console.warn('Could not render recently viewed products:', e);
    }
  }

  // 7. Interactive Style Quiz
  const quizForm = document.getElementById('StyleQuizForm');
  if (quizForm) {
    const steps = quizForm.querySelectorAll('.quiz-step');
    let currentStep = 0;

    quizForm.addEventListener('click', (e) => {
      const optionCard = e.target.closest('.quiz-card');
      if (optionCard) {
        const step = optionCard.closest('.quiz-step');
        step.querySelectorAll('.quiz-card').forEach(c => c.classList.remove('is-selected'));
        optionCard.classList.add('is-selected');

        const nextStepIdx = parseInt(step.getAttribute('data-step-index'), 10) + 1;
        if (nextStepIdx < steps.length) {
          steps.forEach(s => s.classList.remove('is-active'));
          steps[nextStepIdx].classList.add('is-active');
        } else {
          // Final Step - Show Result
          const resultsPanel = document.getElementById('QuizResults');
          if (resultsPanel) {
            quizForm.style.display = 'none';
            resultsPanel.style.display = 'block';
          }
        }
      }
    });
  }

  // 8. Mobile Sticky Add to Cart Observer
  const mainAddToCart = document.querySelector('.btn-add-to-cart');
  const stickyBar = document.querySelector('.sticky-add-to-cart');
  if (mainAddToCart && stickyBar && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
          stickyBar.classList.add('is-visible');
        } else {
          stickyBar.classList.remove('is-visible');
        }
      });
    }, { threshold: 0 });
    observer.observe(mainAddToCart);
  }

  // 9. Smooth In-Page Section Anchor Navigation
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#"]');
    if (link) {
      const href = link.getAttribute('href');
      const hashIndex = href.indexOf('#');
      if (hashIndex !== -1) {
        const path = href.substring(0, hashIndex);
        const targetId = href.substring(hashIndex + 1);
        if (targetId && (path === '' || path === '/' || path === window.location.pathname)) {
          const targetEl = document.getElementById(targetId);
          if (targetEl) {
            e.preventDefault();
            const mobileDrawer = document.getElementById('MobileNavDrawer');
            const backdrop = document.getElementById('DrawerBackdrop');
            if (mobileDrawer && mobileDrawer.classList.contains('is-open')) {
              mobileDrawer.classList.remove('is-open');
              if (backdrop) backdrop.classList.remove('is-open');
              document.body.style.overflow = '';
            }
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.pushState(null, null, '#' + targetId);
          }
        }
      }
    }
  });
});

// Indian Market Pincode Checker
window.checkIndianPincode = function() {
  const input = document.getElementById('PincodeInput');
  const result = document.getElementById('PincodeResult');
  if (!input || !result) return;
  const val = input.value.trim();
  if (!/^\d{6}$/.test(val)) {
    result.style.display = 'block';
    result.innerHTML = '<span style="color: #EF4444;">⚠️ Please enter a valid 6-digit Indian PIN code (e.g. 110001, 400001).</span>';
    return;
  }
  
  result.style.display = 'block';
  let estDays = '2-3 business days';
  let metroCity = '';
  if (val.startsWith('11') || val.startsWith('12') || val.startsWith('20')) {
    metroCity = 'Delhi NCR'; estDays = '1-2 business days (Same/Next Day Dispatch)';
  } else if (val.startsWith('40') || val.startsWith('41')) {
    metroCity = 'Mumbai / Pune'; estDays = '2 business days';
  } else if (val.startsWith('56')) {
    metroCity = 'Bengaluru'; estDays = '2 business days';
  } else if (val.startsWith('50')) {
    metroCity = 'Hyderabad'; estDays = '2-3 business days';
  } else if (val.startsWith('60')) {
    metroCity = 'Chennai'; estDays = '2-3 business days';
  } else if (val.startsWith('70')) {
    metroCity = 'Kolkata'; estDays = '2-3 business days';
  } else {
    estDays = '3-5 business days';
  }

  result.innerHTML = `
    <div style="background: #F0FDF4; border: 1px solid #BBF7D0; padding: 0.6rem 0.8rem; border-radius: 4px; color: #166534;">
      <div style="font-weight: 700; margin-bottom: 2px;">✓ Serviceable to PIN <strong>${val}</strong> ${metroCity ? `(${metroCity})` : ''}</div>
      <div>⚡ Delivery: <strong>${estDays}</strong> via Bluedart / Delhivery Air</div>
      <div>💵 <strong>Cash on Delivery (COD) Available</strong></div>
      <div>✂️ <strong>Complimentary 7-Day Doorstep Exchange</strong></div>
    </div>
  `;
};


// ==========================================
// Policy Modal & Smart Hash Navigation
// ==========================================
(function() {
  function getPolicyModal() { return document.getElementById('PolicyModal'); }
  function getBackdrop() { return document.getElementById('DrawerBackdrop'); }

  function switchPolicyTab(tabName) {
    const termsPane = document.getElementById('PolicyModalContentTerms');
    const returnPane = document.getElementById('PolicyModalContentReturn');
    const tabBtns = document.querySelectorAll('.policy-tab-btn');

    tabBtns.forEach(btn => {
      const isTarget = btn.getAttribute('data-policy-tab') === tabName;
      btn.classList.toggle('is-active', isTarget);
      if (isTarget) {
        btn.style.background = '#10182A';
        btn.style.color = '#FFFFFF';
        btn.style.borderColor = '#10182A';
      } else {
        btn.style.background = 'transparent';
        btn.style.color = 'var(--color-primary-navy, #10182A)';
        btn.style.borderColor = 'var(--color-border, #E2E8F0)';
      }
    });

    if (termsPane) termsPane.style.display = (tabName === 'terms') ? 'block' : 'none';
    if (returnPane) returnPane.style.display = (tabName === 'return') ? 'block' : 'none';
    const modal = getPolicyModal();
    if (modal) modal.scrollTop = 0;
  }

  function openPolicyModal(tabName) {
    const modal = getPolicyModal();
    const backdrop = getBackdrop();
    if (!modal) return;

    modal.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    switchPolicyTab(tabName || 'terms');
  }

  function closePolicyModal() {
    const modal = getPolicyModal();
    const backdrop = getBackdrop();
    if (!modal) return;

    modal.classList.remove('is-open');
    if (backdrop && !document.querySelector('.cart-drawer.is-open') && !document.querySelector('#QuickViewModal.is-open')) {
      backdrop.classList.remove('is-open');
    }
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-open-policy]');
    if (trigger) {
      e.preventDefault();
      const tab = trigger.getAttribute('data-open-policy');
      openPolicyModal(tab);
      return;
    }

    const tabBtn = e.target.closest('.policy-tab-btn');
    if (tabBtn) {
      e.preventDefault();
      const tab = tabBtn.getAttribute('data-policy-tab');
      switchPolicyTab(tab);
      return;
    }

    const closeBtn = e.target.closest('[data-action="close-policy-modal"]');
    if (closeBtn || (e.target === getPolicyModal())) {
      closePolicyModal();
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      const modal = getPolicyModal();
      if (modal && modal.classList.contains('is-open')) {
        closePolicyModal();
      }
    }
  });

  window.addEventListener('DOMContentLoaded', function() {
    const hash = (window.location.hash || '').toLowerCase();
    if (hash === '#terms' || hash === '#terms-and-conditions') {
      openPolicyModal('terms');
    } else if (hash === '#returns' || hash === '#return-policy' || hash === '#refund') {
      openPolicyModal('return');
    }
  });
})();
